// import { DriverStatus, VehicleStatus } from '@repo/shared' // only fed the commented stat cards
import { createFileRoute } from '@tanstack/react-router'
// import { Route as RouteIcon, Truck, Users } from 'lucide-react' // stat-card icons (commented)
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import PageHeader from '@/components/layout/page-header'
import { ActionCenter } from '@/features/dashboard/components/action-center'
import { AnalyticsKpiRow } from '@/features/dashboard/components/analytics-kpi-row'
import { AnalyticsPeriodToggle } from '@/features/dashboard/components/analytics-period-toggle'
import { DriversStatusSection } from '@/features/dashboard/components/drivers-status-section'
import { FleetRankingCard } from '@/features/dashboard/components/fleet-ranking-card'
// import { RoutesSection } from '@/features/dashboard/components/routes-section'
// import { StatCard } from '@/features/dashboard/components/stat-card' // commented: KPIs moved into top row
// import { SystemAlertsSection } from '@/features/dashboard/components/system-alerts-section'
import { VehicleStatusSection } from '@/features/dashboard/components/vehicle-status-section'
import { WeeklyActivityChart } from '@/features/dashboard/components/weekly-activity-chart'
import { useActionCenter } from '@/features/dashboard/hooks/use-action-center'
import { useFleetAnalytics } from '@/features/dashboard/hooks/use-fleet-analytics'
import { useGetDrivers } from '@/features/drivers/hooks/use-get-drivers'
import { useVehicles } from '@/features/vehicles/hooks/use-vehicles'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

// import { useTasks } from '@/hooks/api-hooks'

export const Route = createFileRoute('/dashboard/')({
  head: () => ({ meta: [{ title: localizedPageTitle('dashboard') }] }),
  component: DashboardPage,
})

function DashboardPage() {
  useDocumentTitle('dashboard')
  const { t } = useTranslation()
  const [period, setPeriod] = useState<'week' | 'month' | 'lastMonth'>('week')
  const {
    data: analytics,
    isLoading: analyticsLoading,
    isError: analyticsError,
    refetch: refetchAnalytics,
  } = useFleetAnalytics(period)
  const {
    data: alerts,
    isLoading: alertsLoading,
    isError: alertsError,
    refetch: refetchAlerts,
  } = useActionCenter()
  const { data: vehicles, isLoading: vehiclesLoading } = useVehicles({
    hasDevice: true,
  })
  const { data: drivers, isLoading: driversLoading } = useGetDrivers()
  // Stat-card counts — commented together with the top stat-card grid below:
  // const statsLoading = vehiclesLoading || driversLoading
  // const totalVehicles = vehicles?.length ?? 0
  // const activeVehicles =
  //   vehicles?.filter((v) => v.status === VehicleStatus.Active).length ?? 0
  // const totalDrivers = drivers?.length ?? 0
  // const activeDrivers =
  //   drivers?.filter((d) => d.status === DriverStatus.Driving).length ?? 0

  return (
    <div className="space-y-4">
      {/* Mobile: the period toggle floats just under the navbar and stays put
          while scrolling, so the range can be switched from anywhere on the page.
          (Desktop keeps it inline in the header.) */}
      <div className="bg-background/85 supports-[backdrop-filter]:bg-background/70 sticky -top-4 z-30 -mx-4 -mt-4 border-b px-4 py-2.5 backdrop-blur md:hidden">
        <AnalyticsPeriodToggle value={period} onChange={setPeriod} fullWidth />
      </div>

      <div className="relative">
        <PageHeader
          title={t('dashboard:title')}
          subtitle={t('dashboard:subtitle')}
          hideBackButton
        />
        <div className="hidden md:absolute md:top-1/2 md:right-6 md:-translate-y-1/2 md:block">
          <AnalyticsPeriodToggle value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* Stats Cards — commented 2026-06-04: vehicle/driver/route counts already live in the
          bottom status sections, "Aktivne rute" was hardcoded 0. To restore:
          uncomment this block plus the count vars and the imports above.
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="primary-bg-gradient flex min-h-[140px] min-w-0 flex-col items-center justify-center rounded-xl p-6 sm:min-h-[170px]">
          <img src="/groute-news-logo.png" alt="Groute News" className="h-auto w-40" />
        </div>
        <StatCard title={t('dashboard:totalVehicles')} value={totalVehicles} subtitle={t('dashboard:activeVehiclesCount', { count: activeVehicles })} icon={Truck} linkLabel={t('dashboard:vehicles')} linkTo="/dashboard/vehicles" isLoading={statsLoading} />
        <StatCard title={t('dashboard:totalRoutes')} value={0} subtitle={t('dashboard:routesFinished', { count: 0 })} icon={RouteIcon} linkLabel={t('dashboard:routes')} linkTo="/dashboard/calculate-route" isLoading={statsLoading} />
        <StatCard title={t('dashboard:totalDrivers')} value={totalDrivers} subtitle={t('dashboard:activeDriversCount', { count: activeDrivers })} icon={Users} linkLabel={t('dashboard:drivers')} linkTo="/dashboard/drivers" isLoading={statsLoading} />
      </div>
      */}

      {/* Analytics KPI Row */}
      <AnalyticsKpiRow
        data={analytics}
        isLoading={analyticsLoading}
        isError={analyticsError}
        onRetry={() => refetchAnalytics()}
      />

      {/* Action Center — replaces the old hardcoded SystemAlertsBanner */}
      <ActionCenter
        data={alerts}
        isLoading={alertsLoading}
        isError={alertsError}
        onRetry={() => refetchAlerts()}
      />

      {/* Analytics Chart + Leaderboard */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <WeeklyActivityChart
            data={analytics}
            isLoading={analyticsLoading}
            isError={analyticsError}
            period={period}
          />
        </div>
        <div className="lg:col-span-1">
          <FleetRankingCard
            data={analytics}
            isLoading={analyticsLoading}
            isError={analyticsError}
          />
        </div>
      </div>

      {/* News Carousel */}
      {/* <NewsCarousel /> */}

      {/* Bottom status section — Routes panel commented 2026-06-05: no trips/routes
          data model exists yet (RoutesSection received routes={[]}); trips are a
          separate future feature. Restore by re-adding RoutesSection + its import
          and switching back to lg:grid-cols-3. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <VehicleStatusSection vehicles={vehicles} isLoading={vehiclesLoading} />
        <DriversStatusSection drivers={drivers} isLoading={driversLoading} />
        {/* <RoutesSection
          routes={[]}
          drivers={drivers?.map((d) => ({
            id: d.id,
            name: `${d.firstName} ${d.lastName}`,
          }))}
          isLoading={false}
        /> */}
      </div>
    </div>
  )
}
